import React, { useState } from "react";
import { trackEvent } from "@leafly-com/web-utils";
import { useDispatch, useSelector } from "react-redux";

import publicConfig from "config/public";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { updateLocation } from "redux/action-creators/location";
import { getReloadPageAfterLocationChange } from "redux/selectors/header";
import { convertGooglePlaceIdToLocation } from "utils/convertGoogleGeocoderToLocation";
import { setCookie } from "utils/cookies";
import getCurrentGeolocationPosition from "utils/getCurrentGeolocationPosition";
import initializeGoogleMapsAutocompleteService from "utils/maps/initializeGoogleMapsAutocompleteService";

import Button from "components/botanic/Button";
import Input from "components/botanic/Input";
import LocationSearchIcon from "components/Icons/location_search.svg";
import SearchIcon from "components/Icons/search.svg";
import ClearIcon from "components/Icons/x.svg";

let focusedElement: number | null = null;

type Props = {
  trackingSource: string;
};

const LocationForm: React.FC<Props> = ({ trackingSource }) => {
  const [newLocationValue, setLocationValue] = useState("");
  const [locationError, setLocationError] = useState("");
  const [locationOptions, setLocationOptions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const { cookieDomain, cookieDomainCa } = publicConfig;

  const dispatch = useDispatch();
  const countryCode = useDomainCountryCode();
  const shouldRefreshLocation = useSelector(getReloadPageAfterLocationChange);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const locationValue = e.target.value;
    setLocationValue(locationValue);
    getGeocoderOptions(locationValue);
  };

  const handleSubmit = async (
    option: google.maps.places.AutocompletePrediction
  ) => {
    const userLocation = option ? option : locationOptions[0];

    if (locationOptions.length > 0) {
      await getGeocoderLocation(userLocation);
    }
  };

  const handleBlur = (idx: number) => {
    setTimeout(() => {
      if (idx === focusedElement) {
        clearField();
      }
    }, 100);
  };

  const handleFocus = (idx: number) => {
    focusedElement = idx;
  };

  const clearField = () => {
    setLocationValue("");
    setLocationOptions([]);
    setLocationError("");
  };

  const getGeocoderLocation = async (
    userLocation: google.maps.places.AutocompletePrediction
  ) => {
    let location;
    try {
      location = await convertGooglePlaceIdToLocation(userLocation.place_id);
    } catch (status) {
      setLocationError(
        status === "ZERO_RESULTS"
          ? "Not found. Please try new address or zip code."
          : status
      );
    }

    const cookieLocation = { ...userLocation, ...location };
    updateLocation(dispatch, cookieLocation);

    setCookie(
      "leafly-location-selected",
      true,
      countryCode === "CA" ? cookieDomainCa : cookieDomain
    );
    clearField();

    window.location.assign("/dispensaries");
  };

  const getGeocoderOptions = async (locationValue: string) => {
    if (locationValue) {
      const autocomplete = await initializeGoogleMapsAutocompleteService();
      autocomplete?.getPlacePredictions(
        { input: locationValue, types: ["geocode"] },
        (
          results: google.maps.places.AutocompletePrediction[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (results && status === "OK") {
            setLocationOptions(results);
            if (locationError) {
              setLocationError("");
            }
          } else {
            setLocationOptions([]);
            setLocationError(
              status === "ZERO_RESULTS"
                ? "Not found. Please try a different address or location"
                : status
            );
          }
        }
      );
    } else {
      setLocationOptions([]);
    }
  };

  const handleGeolocateClick = () => {
    trackEvent("Button", "Click", `${trackingSource} location-services Search`);
    getCurrentGeolocationPosition(
      dispatch,
      () => {
        window.location.assign("/dispensaries");
      },
      { reloadPageOnSuccess: shouldRefreshLocation }
    );
  };

  const handleOptionSelection = (
    option: google.maps.places.AutocompletePrediction
  ) => {
    trackEvent(
      "Button",
      "Click",
      `${trackingSource} Text Field Location Search`
    );
    handleSubmit(option);
  };

  return (
    <div className="pb-md lg:pb-lg">
      <div className="my-sm">
        <div className="flex flex-col loc-form-max-height lg:flex-row lg:col lg:col-8 p-none items-center">
          <Button
            onClick={handleGeolocateClick}
            variant="tertiary"
            className="my-sm flex flex-row justify-center text-sm"
            size="full"
            data-testid="location-services-button"
          >
            <LocationSearchIcon height="20" width="20" className="mx-xs" />
            use my exact location
          </Button>
          <div className="text-center text-default text-xs lg:m-md whitespace-nowrap">
            {" "}
            -or-{" "}
          </div>
          <div className="my-sm relative w-full">
            <Input
              autoComplete="off"
              id="finder--hero-location-input"
              data-testid="finder-location-picker-input"
              type="text"
              placeholder="Enter my location manually..."
              className="bg-white form__field--active border-light-grey border rounded lg:m-none text-sm h-full"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleChange(e);
              }}
              onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                e.key === "Enter" && handleOptionSelection
              }
              aria-label={"Enter my location manually"}
              errorMessage={locationError}
              value={newLocationValue}
              onBlur={() => handleBlur(-1)}
            />
            {newLocationValue.length === 0 ? (
              <SearchIcon
                height="20"
                width="20"
                className="absolute right-0 top-0 m-4"
              />
            ) : (
              <button
                onClick={clearField}
                tabIndex={0}
                onKeyPress={clearField}
                aria-label="close"
                className="absolute top-0 right-0 m-4"
              >
                <ClearIcon height="20" width="20" />
              </button>
            )}
            <div className="absolute bg-white border-1 border-leafly-gray w-full z-[1] top-13 shadow">
              {locationOptions.map((option, idx) => (
                <div
                  key={option.description + idx}
                  role="button"
                  tabIndex={0}
                  className="rounded-none col p-sm relative border border-light-grey hover:bg-light-grey max-w-full"
                  onClick={() => handleOptionSelection(option)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && handleOptionSelection(option)
                  }
                  onBlur={() => handleBlur(idx)}
                  onFocus={() => handleFocus(idx)}
                  data-testid={`location-option-${idx}`}
                >
                  {option.description}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="text-xs text-default pt-xs">
          We do not share your location with anyone.
        </div>
      </div>
    </div>
  );
};

export default LocationForm;
